import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaPrint,
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DeadStockReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const primaryButtonClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;
  const secondaryButtonClass = "bg-gray-200 hover:bg-gray-300 text-gray-800";
  const navigate = useNavigate();

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = {
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": branchId,
    "x-branch": branch,
    "x-userid": userId,
  };

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // Form state
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(branchId || "");
  const [itemCategoryList, setItemCategoryList] = useState([]);
  const [selectedItemCategory, setSelectedItemCategory] = useState("");
  const [itemNameList, setItemNameList] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState("");
  const [itemSubCategoryList, setItemSubCategoryList] = useState([]);
  const [selectedItemSubCategory, setSelectedItemSubCategory] = useState("");
  const [itemcode, setItemCode] = useState("");

  // Data state
  const [data, setData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setLoading(true);

    axios
      .get("https://nissi-india.com/api/fillDDL_dsRpt", {
        headers: commonHeaders,
      })
      .then((res) => {
        if (res.data?.success) {
          setBranchOptions(res.data.branch || []);
          setItemCategoryList(res.data.itemcategory || []);
        }
      })
      .catch((err) => {
        console.error("Dropdown fetch failed", err);
        toast.error("Failed to load dropdowns");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedBranch && fromDate && toDate) {
      setLoading(true);

      axios
        .get("https://nissi-india.com/api/fillItemName_dsRpt", {
          params: {
            branchid: selectedBranch,
            fromdate: fromDate,
            todate: toDate,
          },
          headers: commonHeaders,
        })
        .then((res) => {
          setItemNameList(res.data?.itemname || []);
        })
        .catch((err) => {
          console.error("Item name fetch failed", err);
          setItemNameList([]);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setItemNameList([]);
    }
  }, [selectedBranch, fromDate, toDate]);

  useEffect(() => {
    if (selectedItemCategory) {
      axios
        .get("https://nissi-india.com/api/getItemSubcategory_dsRpt", {
          params: { itemcategoryid: selectedItemCategory },
          headers: commonHeaders,
        })
        .then((res) => {
          setItemSubCategoryList(res.data?.subcategories || []);
        })
        .catch((err) => {
          console.error("Subcategory fetch failed", err);
          setItemSubCategoryList([]);
        });
    } else {
      setItemSubCategoryList([]);
      setSelectedItemSubCategory("");
    }
  }, [selectedItemCategory]);

  const handleSearch = () => {
    if (!selectedBranch || !fromDate || !toDate) {
      toast.error("Please select branch and date range");
      return;
    }

    setLoading(true);

    const params = {
      branchid: selectedBranch,
      itemcode: itemcode || "",
      itemname: selectedItemName || "",
      fromdate: fromDate,
      todate: toDate,
      itemcategoryid: selectedItemCategory || "",
      itemsubcategoryid: selectedItemSubCategory || "",
    };

    axios
      .get("https://nissi-india.com/api/deadStockReport_list", {
        params,
        headers: commonHeaders,
      })
      .then((res) => {
        const rows = res.data?.data || [];
        setData(rows);
        setTotalAmount(res.data?.totalAmount || 0);
        setCurrentPage(1);
        toast.success(`Loaded ${rows.length} records`);
      })
      .catch((err) => {
        console.error("Dead stock report fetch failed", err);
        toast.error("Failed to fetch dead stock report");
        setData([]);
        setTotalAmount(0);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePrint = () => {
    const params = {
      branchid: selectedBranch,
      itemcode: itemcode || "",
      itemname: selectedItemName || "",
      fromdate: fromDate,
      todate: toDate,
      itemcategoryid: selectedItemCategory || "",
      itemsubcategoryid: selectedItemSubCategory || "",
    };

    navigate("/inventory/DeadStockReportPrint", {
      state: {
        params,
        headers: commonHeaders,
      },
    });
  };

  const handleDownloadExcel = () => {
    if (data.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const exportData = data.map((row, index) => ({
      "S.No": index + 1,
      "Branch Name": row.fld_BranchName || "",
      "Item Code": row.fld_itemcode || "",
      "Item Name": row.fld_itemname || "",
      UOM: row.fld_uomname || "",
      Category: row.fld_itemcategory_name || "",
      "Sub Category": row.fld_itemsubCategoryName || "",
      Qty: row.fld_qty || 0,
      Price: row.fld_price || 0,
      Amount: row.amount || 0,
      "Transaction Date": row.fld_transdate || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dead Stock Report");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "DeadStockReport.xlsx");

    toast.success("Excel file downloaded successfully");
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setSelectedBranch(branchId || "");
    setSelectedItemCategory("");
    setSelectedItemName("");
    setSelectedItemSubCategory("");
    setItemCode("");
    setItemNameList([]);
    setItemSubCategoryList([]);
    setData([]);
    setTotalAmount(0);
    setCurrentPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  };

  const totalPages = Math.ceil(data.length / rowsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return data.slice(startIndex, startIndex + rowsPerPage);
  }, [data, currentPage, rowsPerPage]);

  const startRecord = data.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, data.length);

  return (
    <LayoutWrapper>
      <HeaderBar title="DEAD STOCK REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-xl shadow-sm border ${
          isDark
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">Dead Stock Report</h1>
            <p className="text-sm text-gray-500 mt-1">
              Analyze and manage non-moving inventory items
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? primaryButtonClass : secondaryButtonClass
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={handleDownloadExcel}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${secondaryButtonClass}`}
              disabled={data.length === 0}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-lg p-5 mb-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Search Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" /> Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium mb-2">
                  From Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  To Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Branch <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  <option value="">Select Branch</option>
                  {branchOptions.map((b) => (
                    <option key={b.fld_Branchid} value={b.fld_Branchid}>
                      {b.fld_BranchName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Category
                </label>
                <select
                  value={selectedItemCategory}
                  onChange={(e) => setSelectedItemCategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  <option value="">Select Category</option>
                  {itemCategoryList.map((c) => (
                    <option
                      key={c.fld_itemcategoryid}
                      value={c.fld_itemcategoryid}
                    >
                      {c.fld_itemcategory_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Sub Category
                </label>
                <select
                  value={selectedItemSubCategory}
                  onChange={(e) => setSelectedItemSubCategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  <option value="">Select Sub Category</option>
                  {itemSubCategoryList.map((s) => (
                    <option
                      key={s.fld_itemsubCategoryid}
                      value={s.fld_itemsubCategoryid}
                    >
                      {s.fld_itemsubCategoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Name
                </label>
                <select
                  value={selectedItemName}
                  onChange={(e) => setSelectedItemName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                >
                  <option value="">Select Item Name</option>
                  {itemNameList.map((n) => (
                    <option key={n.fld_itemid} value={n.fld_itemid}>
                      {n.fld_itemname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Code
                </label>
                <input
                  type="text"
                  value={itemcode}
                  onChange={(e) => setItemCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  placeholder="Enter item code"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${primaryButtonClass} px-4 py-2 rounded transition-colors`}
              >
                <FaSearch className="w-4 h-4" />
                Search
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                <FaPrint className="w-4 h-4" />
                Print Report
              </button>
            </div>
          </div>
        )}

        <div>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-3">
            <h2 className="text-lg font-semibold">
              Results {data.length > 0 && `(${data.length} records)`}
            </h2>

            {data.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="text-sm text-gray-500">
                  Total Amount:{" "}
                  <span className="font-semibold">{formatCurrency(totalAmount)}</span>
                </span>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500">Rows:</label>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border rounded bg-white text-sm text-gray-800"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : data.length > 0 ? (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item Code
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        UOM
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sub Category
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trans Date
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.fld_BranchName || "-"}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                          {row.fld_itemcode || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {row.fld_itemname || "-"}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.fld_uomname || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {row.fld_itemcategory_name || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {row.fld_itemsubCategoryName || "-"}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.fld_qty ?? "-"}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(row.fld_price || 0)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(row.amount || 0)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.fld_transdate || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  <tfoot className="bg-gray-100">
                    <tr>
                      <td
                        colSpan="8"
                        className="px-4 py-3 text-right text-sm font-semibold text-gray-900"
                      >
                        Total Amount
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(totalAmount)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
                <div className="text-sm text-gray-500">
                  Showing {startRecord} to {endRecord} of {data.length} entries
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded border flex items-center gap-2 ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <FaChevronLeft className="w-3 h-3" />
                    Prev
                  </button>

                  <span className="px-3 py-2 text-sm font-medium">
                    Page {currentPage} of {totalPages || 1}
                  </span>

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages || 1))
                    }
                    disabled={currentPage === totalPages || totalPages === 0}
                    className={`px-3 py-2 rounded border flex items-center gap-2 ${
                      currentPage === totalPages || totalPages === 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    Next
                    <FaChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <FaEye className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No dead stock records
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default DeadStockReport;